import React, { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { ErrorState } from '../../../../../../../../CommonComponents/OPL/AsyncStates/ErrorState/ErrorState';
import { Box } from '../../../../../../../../CommonComponents/OPL/Box/Box';
import { SegmentedControl } from '../../../../../../../../CommonComponents/OPL/SegmentedControl/SegmentedControl';
import {
  AccountPortfolioDTO,
  useAccountPortfolioQuery,
} from '../../../../../../api/useAccountPortfolioQuery';
import { TaxStatusDTO } from '../../../../../../api/useAccountsQuery';
import { HypotheticalReturn } from '../HypotheticalReturn/HypotheticalReturn';
import { AssetAllocationLoading } from './AssetAllocation.loading';
import { AggregatedPositions } from './components/AggregatedPositions/AggregatedPositions';
import { NonSpecificPositions } from './components/NonSpecificPositions/NonSpecificPositions';
import { SpecificPositions } from './components/SpecificPositions/SpecificPositions';
import { TaxStatus } from './components/TaxStatus/TaxStatus';
import { useAssetAllocation } from './useAssetAllocation';
import { useAccountFormContext } from '../../context/useAccountFormContext';

interface Props {
  accountId: string;
}

export const AssetAllocation = ({ accountId }: Props) => {
  const { data, isLoading, isError, refetch } = useAccountPortfolioQuery({ accountId });

  if (isLoading) {
    return <AssetAllocationLoading />;
  }

  if (isError) {
    return <ErrorState onAction={refetch} mt="32" />;
  }

  return <AssetAllocationContent accountPortfolio={data} />;
};

interface ContentProps {
  accountPortfolio?: AccountPortfolioDTO;
}

const AssetAllocationContent = ({ accountPortfolio }: ContentProps) => {
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const [selectedAssetAllocationTaxStatus, setSelectedAssetAllocationTaxStatus] = useState<
    number | null
  >(null);

  const { watch } = useFormContext();

  const [accountTaxStatus, specificPositionsAssetAllocation] = watch([
    'bankAccount.TaxStatus',
    'bankAccount.SpecificPositionsValue',
  ]);

  const { localSaveOptions } = useAccountFormContext();

  const {
    data: { showHypotheticalReturn },
  } = useAssetAllocation(accountPortfolio);

  if (accountTaxStatus !== TaxStatusDTO.Mix && !specificPositionsAssetAllocation) {
    return (
      <>
        {(localSaveOptions ? localSaveOptions.showHypotheticalReturn : showHypotheticalReturn) && (
          <Box py="16" maxWidth="50%">
            <HypotheticalReturn />
          </Box>
        )}

        <NonSpecificPositions />
      </>
    );
  }

  return (
    <>
      <TaxStatus
        selectedTaxStatus={selectedAssetAllocationTaxStatus}
        setSelectedTaxStatus={setSelectedAssetAllocationTaxStatus}
      />

      <SegmentedControl
        id="asset-allocation--sc"
        values={['Summary', 'Specific holdings', 'Non-specific holdings']}
        selectedIndex={selectedTabIndex}
        onChange={setSelectedTabIndex}
        ariaLabel="Select asset allocation type"
        mt="16"
      />

      {selectedTabIndex === 0 && (
        <AggregatedPositions assetAllocationTaxStatus={selectedAssetAllocationTaxStatus} />
      )}

      {selectedTabIndex === 1 && (
        <SpecificPositions assetAllocationTaxStatus={selectedAssetAllocationTaxStatus} />
      )}

      {selectedTabIndex === 2 && (
        <NonSpecificPositions assetAllocationTaxStatus={selectedAssetAllocationTaxStatus} />
      )}
    </>
  );
};
